/* eslint-disable max-lines */

"use client";

import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useState,
} from "react";

import { clearUndoDataBySku } from "@/components/Checkout/Cart/Upsells/UpsellUndoButton/upsellUndoUtils";
import { PROMOSCALP, PROMOSKU } from "@/constants/skus";
import { CartData, CartItem } from "@/types/cart";
import type { SiteTheme } from "@/types/checkout";
import { ProductCategory } from "@/types/magento";
import {
  getCartData,
  removeCartItem,
  updateCartItemQuantity,
  upsellProductRemoved,
  isItemQuizReco,
} from "@/utils/cart";
import {
  CART_STORAGE_KEYS,
  clearCartUndoData,
  clearRemovedItem,
  filterAndUpdateUpsellSkus,
  getCartStorage,
  getUpsellSku,
  getUpsellSkus,
  removeUpsellTracking,
  saveCartUndoData,
  saveRemovedItem,
} from "@/utils/cartStorage";
import { setCookie } from "@/utils/cookies";
import { GA4, GA4option } from "@/utils/ga4Utils";
import { getPropertiesFromDpa, isMultipleSkuDpa } from "@/utils/general";
import { getMaskedId } from "@/utils/getMaskedId";

export interface CartContextType {
  isLoadingAjaxCart: boolean;
  setIsLoadingCartPage: (bool: boolean) => void;
  setIsCartFetching: Dispatch<SetStateAction<boolean>>;
  isCartFetching: boolean;
  cartData: CartData | undefined;
  cartNum: number;
  cartLength: number;
  multipleSkusAdded: boolean;
  hasError: boolean;
  activeElement: Element | null | false;
  skusInFlight: string[][];
  setMultipleSkusAdded: (bool: boolean) => void;
  setCartNum: (num: number) => void;
  setCart: (cartPayload: CartData) => void;
  /** Gendered theme signal from the checkout page's GraphQL cart `site` field. */
  siteTheme?: SiteTheme;
  setSiteTheme?: (site: SiteTheme) => void;
  /** Clear cart state (e.g. after order placed). Use with clearAllCartStorage() for full reset. */
  clearCart?: () => void;
  addItems: (url: string, successCallback?: () => void) => void;
  // cart page specific
  isLoadingCartPage: boolean;
  handleQuantityChange: (product: CartItem, qty: number, prevQty: number) => void;
  removeItem: (product: CartItem) => void;
  onCartUpdate: () => Promise<void>;
  cartPageToastMessage: string | null;
  setCartPageToastMessage: (message: string | null) => void;
}

const defaultState: CartContextType = {
  isLoadingAjaxCart: false,
  setIsLoadingCartPage: () => {},
  setIsCartFetching: () => {},
  isCartFetching: false,
  cartData: undefined,
  cartNum: 0,
  cartLength: 0,
  multipleSkusAdded: false,
  hasError: false,
  activeElement: false,
  skusInFlight: [],
  setMultipleSkusAdded: () => {},
  setCartNum: () => {},
  setCart: () => {},
  siteTheme: null,
  setSiteTheme: () => {},
  clearCart: () => {},
  addItems: () => {},
  // cart page specific
  isLoadingCartPage: false,
  handleQuantityChange: () => {},
  removeItem: () => {},
  onCartUpdate: async () => {},
  cartPageToastMessage: null,
  setCartPageToastMessage: () => {},
};

const CartContext = createContext<CartContextType>(defaultState);

interface CartProviderProps {
  children: ReactNode;
}

const CartProvider = ({ children }: CartProviderProps) => {
  const [cartData, setCartData] = useState<CartData | undefined>(undefined);
  const [cartNum, setCartNum] = useState<number>(0);
  const [cartLength, setCartLength] = useState<number>(0);
  const [multipleSkusAdded, setMultipleSkusAdded] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [activeElement, setActiveElement] = useState<Element | null | false>(false);
  const [isLoadingAjaxCart, setIsLoadingAjaxCart] = useState<boolean>(false);
  const [isCartFetching, setIsCartFetching] = useState<boolean>(false);
  const [skusInFlight, setSkusInFlight] = useState<string[][]>([]);
  const [isLoadingCartPage, setIsLoadingCartPage] = useState<boolean>(false);
  const [cartPageToastMessage, setCartPageToastMessage] = useState<string | null>(null);
  const [siteTheme, setSiteTheme] = useState<SiteTheme>(null);

  /*
   * Wrapper that persists the masked_id cookie synchronously whenever cart data
   * arrives. Guest checkout requires this cookie to identify the cart — setting
   * it here (rather than in a useEffect or only on the cart page) guarantees it
   * is available immediately after any add-to-cart operation, regardless of which
   * page the user is on (PDP, upsell popup, mini-cart, etc.).
   */
  const setCartDataAndCookie = (data: CartData | undefined) => {
    const maskedId = data?.totals?.masked_id;
    if (maskedId) {
      setCookie("masked_id", maskedId, 7);
    }
    const cartId = data?.totals?.cart_id;
    if (cartId) {
      setCookie("cart_id", cartId, 7);
    }
    const quote_id =
      typeof data?.items === "object" && !Array.isArray(data?.items)
        ? Object.values(data?.items)?.[0]?.quote?.quote_id
        : undefined;
    if (quote_id) {
      setCookie("quote_id", cartId, 7);
    }
    setCartData(data);
  };

  const getCartTotalItems = (items: Record<string, CartItem> = {}) => {
    let totalItems = 0;

    Object.keys(items).forEach((key) => {
      if (items[key]?.quote?.qty) {
        totalItems += items[key].quote.qty;
      }
    });

    setCartLength(Object.keys(items).length);
    setCartNum(totalItems);
  };

  useEffect(() => {
    if (cartData && cartData.items) {
      getCartTotalItems(cartData.items);
    } else {
      // @ts-expect-error - this is a workaround for a bug in the browser
      if (cartData && cartData !== "Quote does not exist.") {
        /*
         * Assume cartData[1] is Record<string, CartItem>
         * Handle case where API returns data in array format
         */
        const items = (cartData as unknown as [unknown, Record<string, CartItem>])[1];
        if (items) {
          getCartTotalItems(items);
        }
      }
    }
  }, [cartData]);

  const addToCart = async (url: string, successCallback?: () => void) => {
    setActiveElement(document.activeElement);
    setIsLoadingAjaxCart(false);
    setMultipleSkusAdded(isMultipleSkuDpa(url.split("?")[1]));
    const body = getPropertiesFromDpa(url);

    if (body.skus && body.purchase_source) {
      setSkusInFlight((prev) => [...prev, Array.isArray(body.skus) ? body.skus : [body.skus]]);
      setIsLoadingAjaxCart(true);
      setIsLoadingCartPage(true);
      setHasError(false);

      const maskedId = getMaskedId();
      if (maskedId) {
        body.masked_id = maskedId;
      }

      try {
        const res = await fetch(
          `${process.env.NEXT_PUBLIC_MAGENTO_URL}rest/V1/repack/add_to_cart/`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          }
        );

        if (!res.ok) {
          throw new Error(`Failed to add to cart: ${res.status}`);
        }

        const responseData = await res.json();

        // If it's a string, try to parse it. If it's already an object, use it.
        let jsonData = responseData;
        if (typeof responseData === "string") {
          try {
            jsonData = JSON.parse(responseData);
          } catch {
            // If it's a string but NOT valid JSON, we keep the original string
            jsonData = { message: responseData };
          }
        }

        // Check for Magento error flag
        if (jsonData?.error) {
          throw new Error(jsonData.message || `Error: ${res.status}`);
        }

        // Fetch the full cart data after a successful addition
        const data = await getCartData();
        handleCartDiff(data);
        setCartDataAndCookie(data);
        setIsLoadingAjaxCart(false);
        setIsLoadingCartPage(false);
        setSkusInFlight([]);
        if (successCallback) successCallback();
      } catch (error) {
        setSkusInFlight([]);
        setIsLoadingAjaxCart(false);
        setIsLoadingCartPage(false);
        setHasError(true);
        const errorMessage =
          error instanceof Error ? error.message : "Failed to add items to cart. Please try again.";
        setCartPageToastMessage(errorMessage);
        // eslint-disable-next-line no-console
        console.error("Error:", error);
        throw error;
      }
    } else {
      // eslint-disable-next-line no-console
      console.warn("DPA link is incorrect.");
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  const onCartUpdate = async () => {
    try {
      const data = await getCartData();
      handleCartDiff(data);
      setCartDataAndCookie(data);
      scrollToTop();
      setIsLoadingCartPage(false);
    } catch (error) {
      setIsLoadingCartPage(false);
      setHasError(true);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to update cart. Please try again.";
      setCartPageToastMessage(errorMessage);
      // eslint-disable-next-line no-console
      console.error("Error updating cart:", error);
    }
  };

  const removeItem = async (product: CartItem) => {
    const cartId = cartData?.totals?.cart_id;
    const itemId = product.quote?.quote_item_id;

    if (!cartId) {
      // eslint-disable-next-line no-console
      console.warn("Cannot remove item: cart ID is missing");
      setHasError(true);
      setCartPageToastMessage("Unable to remove item. Please try again.");
      return;
    }

    if (!itemId) {
      // eslint-disable-next-line no-console
      console.warn("Cannot remove item: item ID is missing");
      setHasError(true);
      setCartPageToastMessage("Unable to remove item. Please try again.");
      return;
    }

    const qty = product.quote.qty;
    const purchase_source = product?.quote?.purchase_source || "cart";
    const sku = product?.quote?.sku?.replace("-SP01", "-S01");
    const isPromoSku = product?.quote?.sku?.includes("-SP01");
    const isScalpPromo = sku?.includes(PROMOSCALP);
    // Save removedItem data to sessionStorage BEFORE updating cart
    const code =
      typeof cartData?.totals?.coupon_code === "string"
        ? `code=${cartData?.totals?.coupon_code.toUpperCase()}&`
        : "";
    const dpaSfx = isPromoSku ? "promo_product_enabled=true&" : "";
    const purchSourceDpa = `purchase_source=${purchase_source}&`;
    let dpaLink = `${code}${purchSourceDpa}${dpaSfx}skus=${sku}|${qty}`;

    /*
     * console.log(dpaLink);
     * return false;
     */
    setIsLoadingCartPage(true);

    /*
     * /dpa/add/tocart?skus=CAREKIT-[BBLOCKER_N01;MHAIRSERUMSML_N01;ACTIVECLEANSE_N01]&code=ACTFNF
     * NT2-23427 FNF Active Subscriber - workaround for "CAREKIT" sku array
     * we cant determine what code been used if multiple sales rules used in same cart, so for now we use PROMOSKU-> FNFCODE pair
     */

    /*
     * NT2-25057 - updated logic to include WCAREKIT2 and MCAREKIT2
     * DPA for this page uses duplicate skus in sku paramter instead of '|quantity' syntax
     */
    if (PROMOSKU.includes(product.quote.sku)) {
      const skusArray =
        Object.keys(product.child_products).length === 1
          ? `${Object.keys(product.child_products)[0]};${Object.keys(product.child_products)[0]}`
          : Object.keys(product.child_products).join(";");
      dpaLink = `skus=${product.quote.sku}-[${skusArray}]&${purchSourceDpa}`;
    }
    saveRemovedItem("/dpa/add/tocart/?" + dpaLink);

    try {
      const res = await removeCartItem(cartId, itemId);

      if (!res.ok) {
        throw new Error(`Failed to remove item: ${res.status}`);
      }
      /*
       *  Find and remove the matching UndoData from session storage.
       */
      clearUndoDataBySku(product?.quote?.sku);

      await onCartUpdate();
    } catch (error) {
      setIsLoadingCartPage(false);
      setHasError(true);
      // clear the removedItem since the removal failed
      clearRemovedItem();
      // eslint-disable-next-line no-console
      console.warn("Fetch error: ", error);
    }

    /*
     * if they added an upsell from the PDP modal we set the
     * sku in localstorage so we can fire upsell removed here
     */
    const scalpLpUpgradedSkus = getUpsellSkus(CART_STORAGE_KEYS.SCALP_LP_UPGRADED_SKUS);
    const scalpRoutineLpUpgradedSkus = getUpsellSkus(
      CART_STORAGE_KEYS.SCALP_ROUTINE_LP_UPGRADED_SKUS
    );
    const scalpUpsellProducts = getUpsellSkus(CART_STORAGE_KEYS.SCALP_UPGRADED_SKUS);
    const influencerUpsellSkus = getUpsellSkus(CART_STORAGE_KEYS.INFLUENCER_UPSELL_SKUS);
    const scalpAccountUpsellProducts = getUpsellSkus(CART_STORAGE_KEYS.SCALP_ACCOUNT_UPGRADED_SKUS);
    const pdpUpgradedSku = getUpsellSku(CART_STORAGE_KEYS.PDP_UPGRADED_SKU);
    const coupon_code = cartData?.totals?.coupon_code || null;

    if (pdpUpgradedSku === product.sku) {
      upsellProductRemoved(product, "modal", false, coupon_code);
      removeUpsellTracking(CART_STORAGE_KEYS.PDP_UPGRADED_SKU);
    } else if (scalpUpsellProducts && scalpUpsellProducts?.includes(product.sku)) {
      // NT2-9216 upsell Influencer LP | Segment Events
      upsellProductRemoved(product, "influencer_scalp_upsell", false, coupon_code);
      filterAndUpdateUpsellSkus(
        CART_STORAGE_KEYS.SCALP_UPGRADED_SKUS,
        scalpUpsellProducts,
        product.sku
      );
    } else if (scalpAccountUpsellProducts && scalpAccountUpsellProducts?.includes(product.sku)) {
      upsellProductRemoved(product, "account_scalp_upsell", false, coupon_code);
      filterAndUpdateUpsellSkus(
        CART_STORAGE_KEYS.SCALP_ACCOUNT_UPGRADED_SKUS,
        scalpAccountUpsellProducts,
        product.sku
      );
    } else if (scalpLpUpgradedSkus && scalpLpUpgradedSkus?.includes(product.sku)) {
      upsellProductRemoved(product, "scalp_lp_modal_upsell", true);
      filterAndUpdateUpsellSkus(
        CART_STORAGE_KEYS.SCALP_LP_UPGRADED_SKUS,
        scalpLpUpgradedSkus,
        product.sku
      );
    } else if (scalpRoutineLpUpgradedSkus && scalpRoutineLpUpgradedSkus?.includes(product.sku)) {
      upsellProductRemoved(product, "scalp_routine_lp_upsell", true);
      filterAndUpdateUpsellSkus(
        CART_STORAGE_KEYS.SCALP_ROUTINE_LP_UPGRADED_SKUS,
        scalpRoutineLpUpgradedSkus,
        product.sku
      );
    } else if (influencerUpsellSkus && influencerUpsellSkus?.includes(product.sku)) {
      upsellProductRemoved(product, "influencer_lp_upsell", false, coupon_code);
      filterAndUpdateUpsellSkus(
        CART_STORAGE_KEYS.INFLUENCER_UPSELL_SKUS,
        influencerUpsellSkus,
        product.sku
      );
    } else if (isScalpPromo) {
      upsellProductRemoved(product, "cart", false, coupon_code);
    } else {
      // else it's a normal product removal
      /* Cart items use product_gender (ProductGender); GA4 normalisation handled by segmentfunctions */
      const option = {
        ...product,
        old_price: parseFloat(product.old_price),
        bundled_price: parseFloat(product.price),
        quiz_reco: isItemQuizReco(product),
        product_core_category: String(product.core_category) as ProductCategory,
      } as GA4option;
      GA4.onProductRemoved({ options: [option], purchase_source, cartId });
    }
  };

  const handleQuantityChange = async (product: CartItem, qty: number, prevQty: number) => {
    if (qty <= 0) {
      removeItem(product);
      return;
    }

    setIsLoadingCartPage(true);
    const cartId = cartData?.totals?.cart_id;
    const itemId = product.quote?.quote_item_id;
    if (!cartId) {
      // eslint-disable-next-line no-console
      console.warn("Cannot update quantity: cart ID is missing");
      setIsLoadingCartPage(false);
      setHasError(true);
      setCartPageToastMessage("Unable to update quantity. Please try again.");
      return;
    }

    if (!itemId) {
      // eslint-disable-next-line no-console
      console.warn("Cannot update quantity: item ID is missing");
      setIsLoadingCartPage(false);
      setHasError(true);
      setCartPageToastMessage("Unable to update quantity. Please try again.");
      return;
    }
    const purchase_source = product?.quote?.purchase_source || "cart";

    try {
      const res = await updateCartItemQuantity(cartId, itemId, qty);

      if (!res.ok) {
        throw new Error(`Failed to update quantity: ${res.status}`);
      }

      await onCartUpdate();
    } catch (error) {
      setIsLoadingCartPage(false);
      setHasError(true);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to update quantity. Please try again.";
      setCartPageToastMessage(errorMessage);
      // eslint-disable-next-line no-console
      console.warn("Fetch error: ", error);
    }

    const quantity = Math.abs(qty - prevQty);

    /* Cart items use product_gender (ProductGender); GA4 normalisation handled by segmentfunctions */
    const option = {
      ...product,
      old_price: parseFloat(product.old_price),
      bundled_price: parseFloat(product.price),
      quiz_reco: isItemQuizReco(product),
      quantity,
      product_core_category: String(product.core_category) as ProductCategory,
    } as GA4option;
    if (prevQty > qty) {
      GA4.onProductRemoved({ options: [option], purchase_source, cartId });
    } else {
      GA4.onProductAdded({ options: [option], purchase_source, cartId });
    }
  };

  const handleCartDiff = (newCartData: CartData) => {
    /*
     * 1. Safety Check: If there's no cart or items, we can't perform a diff.
     */
    if (!newCartData?.items) return;

    /*
     * 2. Identify States:
     * omniData = The "Last Known State" (from React State or LocalStorage fallback).
     * currentCoupon = The coupon status of the incoming fresh data.
     * prevCoupon = The coupon status of the cart we had before.
     */
    const omniData = cartData || getCartStorage<CartData>(CART_STORAGE_KEYS.CART, "local");
    const currentCoupon = newCartData?.totals?.coupon_code;
    const prevCoupon = omniData?.totals?.coupon_code;

    /*
     * SCENARIO A: The "Healthy State" Cleanup
     * If the user manually restores the coupon or types it in, clear the undo data.
     *
     * SCENARIO B: The "Stale State" Cleanup
     * If the coupon was already missing and the user performs ANOTHER action, the previous UndoData is no longer valid
     *
     */
    if (currentCoupon || !prevCoupon) {
      clearCartUndoData();
    } else if (!currentCoupon && prevCoupon) {
      /*
       * SCENARIO C: Coupon loss detection
       * This block only fires at the specific transition from HAS COUPON to NO COUPON.
       */
      const currentItems = newCartData.items || {};
      const prevItems = omniData?.items || {};
      const cartId = omniData?.totals?.cart_id;

      /*
       * BE swaps promo SKUs (-SP01) to regular (-S01) when the coupon is dropped,
       * so compare by base SKU to avoid treating the swap as an add/remove.
       */
      const baseSku = (sku: string) => sku.replace("-SP01", "-S01");
      const prevByBase = Object.fromEntries(
        Object.values(prevItems).map((item) => [baseSku(item.quote.sku), item])
      );
      const currentBaseSkus = new Set(
        Object.values(currentItems).map((item) => baseSku(item.quote.sku))
      );

      const violator = Object.values(currentItems).find((item) => {
        const prevItem = prevByBase[baseSku(item.quote.sku)];
        // It's a change if: 1. Item didn't exist OR 2. Quantity is different
        return !prevItem || item.quote.qty !== prevItem.quote.qty;
      });
      //  Check for Additions
      if (violator) {
        const prevItem = prevByBase[baseSku(violator.quote.sku)];
        //console.log("violator added", prevItem);
        saveCartUndoData({
          cartId,
          coupon: prevCoupon,
          sku: violator.quote.sku,
          itemId: violator.quote.quote_item_id,
          oldQty: prevItem?.quote?.qty || 0, // 0 = Addition
          qty: violator.quote.qty, // >0 = Modification
        });
      } else {
        //  Check for Removals
        const removedSku = Object.keys(prevItems).find(
          (sku) => !currentBaseSkus.has(baseSku(prevItems[sku].quote.sku))
        );

        if (removedSku) {
          const prevItem = prevItems[removedSku];

          // Filter out free gifts/samples as they can't be re-added via DPA
          const isGift = prevItem?.name?.includes("Gift") || prevItem?.price === "0.00";
          if (prevItem && !isGift) {
            saveCartUndoData({
              cartId,
              coupon: prevCoupon,
              sku: removedSku,
              itemId: null, // No current ID exists
              oldQty: prevItem.quote.qty,
              qty: 0,
            });
          }
        }
      }
    }
  };

  return (
    <CartContext.Provider
      value={{
        isLoadingAjaxCart,
        isLoadingCartPage,
        setIsLoadingCartPage: (bool: boolean) => setIsLoadingCartPage(bool),
        setIsCartFetching,
        isCartFetching,
        cartData,
        cartNum,
        cartLength,
        multipleSkusAdded,
        hasError,
        activeElement,
        skusInFlight,
        setMultipleSkusAdded,
        setCartNum: (num) => {
          const safeNum = typeof num === "number" && !isNaN(num) ? Math.max(0, Math.floor(num)) : 0;
          setCartNum(safeNum);
        },
        clearCart: () => {
          setCartData(undefined);
          setCartNum(0);
          setCartLength(0);
        },
        setCart: (data: CartData) => {
          handleCartDiff(data); // Run diff against stale 'cartData'
          setCartDataAndCookie(data); // Update 'cartData' to the new data
        },
        addItems: addToCart,
        handleQuantityChange,
        removeItem,
        onCartUpdate,
        cartPageToastMessage,
        setCartPageToastMessage,
        siteTheme,
        setSiteTheme,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
export { CartProvider };
